import { TwoFactorMethod, TwoFactorResponse } from "@sp-crm/core";
import { useCallback, useEffect, useState } from "react";
import { reportError } from "util/error-tracking";
import * as http from "util/http";
import { handleEvent } from "util/user-events";

interface UseTwoFactorFlowParams {
    twoFactorData: TwoFactorResponse | null;
    sendCodeEndpoint: string;
    sendCodePayload?: (method: TwoFactorMethod) => Record<string, unknown>;
    onSendCodeError?: (error: unknown, method: TwoFactorMethod) => void;
    eventPrefix?: string;
}

export const useTwoFactorFlow = ({
    twoFactorData,
    sendCodeEndpoint,
    sendCodePayload = method => ({ method }),
    onSendCodeError,
    eventPrefix = "2fa",
}: UseTwoFactorFlowParams) => {
    const [verificationCode, setVerificationCode] = useState("");
    const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod | null>(null);
    const [codeSent, setCodeSent] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);
    const [loading, setLoading] = useState(false);

    const sendCode = useCallback(
        async (method: TwoFactorMethod): Promise<void> => {
            if (!twoFactorData?.userId) {
                reportError(
                    new Error("No user ID available for sending two-factor code"),
                    {
                        twoFactorData,
                    },
                );
                return;
            }

            try {
                setLoading(true);
                await http.post(
                    http.newRequestDescription(),
                    sendCodeEndpoint,
                    sendCodePayload(method),
                );
                setSelectedMethod(method);
                setCodeSent(true);
                setResendCountdown(30);
                if (eventPrefix === "2fa") {
                    handleEvent("2fa-enable-start", { method });
                } else if (eventPrefix === "2fa-password-reset") {
                    handleEvent("2fa-password-reset-start", { method });
                }
            } catch (error) {
                if (eventPrefix === "2fa") {
                    handleEvent("2fa-enable-fail", { method, step: "send-code" });
                } else if (eventPrefix === "2fa-password-reset") {
                    handleEvent("2fa-password-reset-fail", { method, step: "send-code" });
                }
                reportError(error as Error, { method, step: "send-code" });
                if (onSendCodeError) {
                    onSendCodeError(error, method);
                }
            } finally {
                setLoading(false);
            }
        },
        [twoFactorData, sendCodeEndpoint, sendCodePayload, onSendCodeError, eventPrefix],
    );

    const handleResendCode = useCallback(async (): Promise<void> => {
        if (!selectedMethod || resendCountdown > 0) return;
        if (eventPrefix === "2fa") {
            handleEvent("2fa-enable-resent", { method: selectedMethod });
        } else if (eventPrefix === "2fa-password-reset") {
            handleEvent("2fa-password-reset-resend", { method: selectedMethod });
        }
        await sendCode(selectedMethod);
    }, [selectedMethod, resendCountdown, sendCode, eventPrefix]);

    useEffect(() => {
        if (twoFactorData) {
            if (twoFactorData.codeSent && twoFactorData.method) {
                setSelectedMethod(twoFactorData.method);
                setCodeSent(true);
                setResendCountdown(30);
                if (eventPrefix === "2fa") {
                    handleEvent("2fa-enable-start", { method: twoFactorData.method });
                } else if (eventPrefix === "2fa-password-reset") {
                    handleEvent("2fa-password-reset-start", {
                        method: twoFactorData.method,
                    });
                }
            }
        }
    }, [twoFactorData, eventPrefix]);

    useEffect(() => {
        if (resendCountdown > 0) {
            const timer = setTimeout(() => {
                setResendCountdown(resendCountdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [resendCountdown]);

    const resetFlow = useCallback(() => {
        setVerificationCode("");
        setSelectedMethod(null);
        setCodeSent(false);
        setResendCountdown(0);
        setLoading(false);
    }, []);

    return {
        verificationCode,
        setVerificationCode,
        selectedMethod,
        setSelectedMethod,
        codeSent,
        setCodeSent,
        resendCountdown,
        loading,
        setLoading,
        sendCode,
        handleResendCode,
        resetFlow,
    };
};
